import React, { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

// Utilities
import { createExtender } from '../../../../../lib/bemUtils/bem';
import { createDescribedById } from './utilities';

// Enumerables
import { TooltipPlacement } from '../../../../atoms/Tooltip';

// Components
import FormHelp from '../FormHelp/FormHelp';
import { FormRequiredSymbol } from '../FormRequiredSymbol/FormRequiredSymbol';

// Enumerables
import { FormLabelContainerModifier, FormLabelModifier } from './enumerable';
import { Namespace } from '../../../../../translation/namespace';
import { ExtendedInfoSVG } from '../../../../../_lib/components/ExtendedInfo';
import { Modifier } from '../../../../../lib/bemUtils/Modifier';
import { CollapsibleModifier } from '../../../../organisms/CollapsibleOverlay/CollapsibleOverlay.modifier';
import { SVGIcon } from '../../../../atoms/SVG';

const extender = createExtender('form-label');
const blockContainerExtender = createExtender('form-label__container');

type FormLabelType = {
  id?: string;
  htmlFor: string;
  required?: boolean;
  hidden?: boolean;
  tooltipPlacement?: TooltipPlacement;
  children: ReactNode;
  minLength?: number;
  modifier?: FormLabelModifier | FormLabelModifier[];
  containerModifier?: FormLabelContainerModifier | FormLabelContainerModifier[];
  helpText?: string;
  helpTextModifier?: Modifier<CollapsibleModifier>;
  extendedInfoSVG?: {
    helpText: string;
    svgSpriteID: SVGIcon;
    tooltipPlacement: TooltipPlacement;
  };
  type?: string;
  layout?: 'switch';
};

/**
 * Surrounds it's children with a label.
 * With the property helpText a <a href='#formshelp'>FormHelp</a> is added.
 */
export default function FormLabel({
  id,
  htmlFor,
  required = false,
  hidden = false,
  tooltipPlacement,
  children,
  minLength,
  modifier = [],
  containerModifier,
  helpText,
  helpTextModifier,
  extendedInfoSVG,
  layout,
}: FormLabelType) {
  const { t } = useTranslation();

  const getRequiredSymbol = () => {
    if (required) {
      return <FormRequiredSymbol />;
    }
    return null;
  };

  // optional: helpText default is empty
  const getFormsHelpText = () => {
    if (helpText) {
      return (
        <FormHelp
          id={createDescribedById(htmlFor)}
          helpText={helpText}
          collapsibleModifier={helpTextModifier}
          tooltipPlacement={tooltipPlacement || TooltipPlacement.TOP}
        />
      );
    }
    return null;
  };

  const getExtendedInfoSVG = () => {
    if (extendedInfoSVG) {
      return <ExtendedInfoSVG {...extendedInfoSVG} />;
    }

    return null;
  };

  /**
   * Returns the minLength information values into the label
   * @return {null|*}
   */
  const getLengthConstraintsHelptext = () => {
    if (minLength) {
      return (
        <span className="form-label__length">
          {'\u0020('}
          {minLength && t(`${Namespace.phrases}:minLength`, { minLength })} {t(`${Namespace.phrases}:characters`)})
        </span>
      );
    }

    return null;
  };

  /** Returns the different possible contents
   * - regular children (can be mostly anything)
   * - minLength info
   * - Required symbol
   * @return {*}
   */
  const getLabelContent = (): ReactNode => {
    return (
      <>
        {children}
        {getLengthConstraintsHelptext()}
        {getRequiredSymbol()}
        {layout === 'switch'
          ? (
              <span
                className="form-label__switch"
                aria-hidden="true" // on/off is hidden for screen readers (part of the semantic checkbox)
                data-switch-on={t(`${Namespace.FormLabel}:on`)}
                data-switch-off={t(`${Namespace.FormLabel}:off`)}
              />
            )
          : null}
      </>
    );
  };

  /**
   * Determines, if label content must be hidden or not
   * Important in Checkboxes!
   * @return {*}
   */
  const hideLabelContentOrNot = (): ReactNode => {
    if (hidden) {
      return <span className="invisible">{getLabelContent()}</span>;
    }
    return getLabelContent();
  };

  const getModifier = () => {
    if (hidden && modifier && Array.isArray(modifier)) {
      modifier?.push(FormLabelModifier.HIDDEN);
    }
    return modifier;
  };

  return (
    <span className={blockContainerExtender(containerModifier as Modifier<string>)}>
      <label id={id} htmlFor={htmlFor} className={extender(getModifier())}>
        {hideLabelContentOrNot()}
      </label>
      {getFormsHelpText()}
      {getExtendedInfoSVG()}
    </span>
  );
}
